import React, { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import cccApi, {
  RetencaoMarcacao,
  TentativasContactoData,
  TentativaContactoItem,
} from '../api/cccApi';
import { formatAttemptDateTime, nowTimeHm, todayIsoDate } from '../config/tentativasContacto';

type Props = {
  marcacaoId: string;
  initial?: TentativasContactoData | null;
  disabled?: boolean;
  onSaved?: (marcacao: RetencaoMarcacao) => void;
};

const formatSavedAt = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso.replace('Z', '')).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const parseItems = (initial?: TentativasContactoData | null): TentativaContactoItem[] => {
  const rows = initial?.items;
  if (!rows?.length) return [];
  return rows.map((row) => ({
    id: row.id || crypto.randomUUID(),
    date: row.date || '',
    time: row.time || '',
    created_at: row.created_at,
    created_by: row.created_by,
    created_by_name: row.created_by_name,
  }));
};

const RetencaoTentativasContactoForm: React.FC<Props> = ({
  marcacaoId,
  initial,
  disabled,
  onSaved,
}) => {
  const [items, setItems] = useState<TentativaContactoItem[]>(parseItems(initial));
  const [attemptDate, setAttemptDate] = useState(() => todayIsoDate());
  const [attemptTime, setAttemptTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<{
    updated_at?: string | null;
    updated_by_name?: string | null;
  }>({
    updated_at: initial?.updated_at,
    updated_by_name: initial?.updated_by_name,
  });

  useEffect(() => {
    setItems(parseItems(initial));
    setMeta({
      updated_at: initial?.updated_at,
      updated_by_name: initial?.updated_by_name,
    });
  }, [marcacaoId, initial?.items, initial?.updated_at, initial?.updated_by_name]);

  useEffect(() => {
    setAttemptDate(todayIsoDate());
    setAttemptTime('');
  }, [marcacaoId]);

  const previewLabel = useMemo(
    () => formatAttemptDateTime(attemptDate, attemptTime || '—'),
    [attemptDate, attemptTime]
  );

  const persistItems = async (nextItems: TentativaContactoItem[]) => {
    if (disabled || saving) return false;
    setSaving(true);
    try {
      const res = await cccApi.updateRetencaoTentativasContacto(marcacaoId, {
        items: nextItems.map((row) => ({
          id: row.id,
          date: row.date,
          time: row.time,
        })),
      });
      if (!res.success || !res.marcacao) {
        message.error(res.message || 'Não foi possível guardar as tentativas');
        return false;
      }
      const saved = res.marcacao.retencao_claim?.tentativas_contacto;
      if (saved?.items) {
        setItems(parseItems(saved));
      } else {
        setItems([]);
      }
      setMeta({
        updated_at: saved?.updated_at,
        updated_by_name: saved?.updated_by_name,
      });
      onSaved?.(res.marcacao);
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao guardar tentativas de contacto';
      message.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUseNow = () => {
    setAttemptDate(todayIsoDate());
    setAttemptTime(nowTimeHm());
  };

  const handleAddAttempt = async () => {
    const date = attemptDate.trim();
    const time = attemptTime.trim();
    if (!date) {
      message.warning('Indique a data da tentativa');
      return;
    }
    if (!time) {
      message.warning('Indique a hora da tentativa');
      return;
    }
    const nextItems: TentativaContactoItem[] = [
      {
        id: crypto.randomUUID(),
        date,
        time,
      },
      ...items,
    ];
    const ok = await persistItems(nextItems);
    if (ok) {
      message.success('Tentativa de contacto registada');
      setAttemptDate(todayIsoDate());
      setAttemptTime('');
    }
  };

  const handleRemove = async (index: number) => {
    if (index < 0 || index >= items.length) return;
    const nextItems = items.filter((_, i) => i !== index);
    const ok = await persistItems(nextItems);
    if (ok) {
      message.success('Tentativa removida');
    }
  };

  return (
    <div className="rec-tentativas-form">
      <section className="rec-tentativas-add">
        <h4 className="rec-tentativas-section-title">Nova tentativa</h4>
        <p className="rec-tentativas-hint">
          Registe o dia e a hora em que tentou contactar o cliente.
        </p>

        <div className="rec-tentativas-fields">
          <label className="rec-tentativas-field">
            <span className="rec-tentativas-label">
              Data <span className="rec-required-asterisk">*</span>
            </span>
            <input
              type="date"
              className="rec-tentativas-input"
              value={attemptDate}
              onChange={(e) => setAttemptDate(e.target.value)}
              disabled={disabled || saving}
            />
          </label>
          <label className="rec-tentativas-field">
            <span className="rec-tentativas-label">
              Hora <span className="rec-required-asterisk">*</span>
            </span>
            <input
              type="time"
              className="rec-tentativas-input"
              value={attemptTime}
              onChange={(e) => setAttemptTime(e.target.value)}
              disabled={disabled || saving}
            />
          </label>
        </div>

        <p className="rec-tentativas-preview" aria-live="polite">
          {attemptTime ? (
            <>
              Será registado: <strong>{previewLabel}</strong>
            </>
          ) : (
            <>Selecione a hora ou use &quot;Agora&quot; para preencher data e hora atuais.</>
          )}
        </p>

        <div className="rec-tentativas-actions">
          <button
            type="button"
            className="rec-btn rec-btn-secondary"
            onClick={handleUseNow}
            disabled={disabled || saving}
          >
            Agora
          </button>
          <button
            type="button"
            className="rec-btn rec-btn-primary"
            onClick={handleAddAttempt}
            disabled={disabled || saving}
          >
            {saving ? 'A guardar…' : 'Adicionar tentativa'}
          </button>
        </div>
      </section>

      <section className="rec-tentativas-list">
        <h4 className="rec-tentativas-section-title">
          Tentativas registadas ({items.length})
        </h4>
        {items.length === 0 ? (
          <p className="rec-tentativas-empty">Ainda não há tentativas de contacto.</p>
        ) : (
          <ul className="rec-tentativas-items">
            {items.map((row, index) => (
              <li key={row.id || `${row.date}-${row.time}-${index}`} className="rec-tentativas-item">
                <div className="rec-tentativas-item-main">
                  <span className="rec-tentativas-item-when">
                    {formatAttemptDateTime(row.date, row.time)}
                  </span>
                  {row.created_by_name && (
                    <span className="rec-tentativas-item-meta">
                      por {row.created_by_name}
                      {row.created_at ? ` · ${formatSavedAt(row.created_at)}` : ''}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="rec-btn rec-btn-ghost rec-tentativas-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRemove(index);
                  }}
                  disabled={disabled || saving}
                  title="Remover tentativa"
                  aria-label={`Remover tentativa de ${formatAttemptDateTime(row.date, row.time)}`}
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {meta.updated_at && (
        <p className="rec-tentativas-saved-meta">
          Última atualização: {formatSavedAt(meta.updated_at)}
          {meta.updated_by_name ? ` · ${meta.updated_by_name}` : ''}
        </p>
      )}
    </div>
  );
};

export default RetencaoTentativasContactoForm;
